import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Container } from 'reactstrap'

import { SpotlightDetail } from '#app/components/spotlights/SpotlightDetail.tsx'
import { articleJsonLd, jsonLdScript } from '#app/helpers/agentDiscovery/jsonLd.ts'
import {
  metaTitleAndDescription,
  truncateMetaDescription,
} from '#app/helpers/metaTags.helper.ts'
import {
  encodeDynamicContentBannerUrl,
  fetchContentBySlug,
} from '#app/models/content.server.ts'
import { recordDynamicContentWebFetchSafe } from '#app/models/surface-access.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { renderSpotlightMarkdownToHtml } from '#app/utils/spotlight-markdown.server.ts'

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) {
    return metaTitleAndDescription({
      title: 'Novidade | Clube do Tennis',
      description: 'Novidade do Clube do Tennis.',
      content_type: 'website',
    })
  }

  const rawDescription =
    data.summary?.trim() || 'Novidade do Clube do Tennis.'
  const description = truncateMetaDescription(rawDescription)
  const title = `${data.title} | Clube do Tennis`

  const tags = metaTitleAndDescription({
    title,
    description,
    content_type: 'article',
    image: data.bannerImageUrl ?? undefined,
    canonicalUrl: data.canonicalUrl,
  })

  return [
    ...tags,
    jsonLdScript(
      articleJsonLd({
        headline: data.title,
        description,
        url: data.canonicalUrl,
        image: data.bannerImageUrl ?? undefined,
        datePublished: data.publishAt,
      }),
    ),
  ]
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  const slugRaw = params.slug?.trim() ?? ''
  const slug = slugRaw ? decodeURIComponent(slugRaw) : ''
  if (!slug) {
    throw new Response('Not found', { status: 404 })
  }

  const row = await fetchContentBySlug(slug, { viewer: null })
  if (!row) {
    throw new Response('Not found', { status: 404 })
  }

  const bannerImageUrl = encodeDynamicContentBannerUrl(row.bannerImageUrl)
  const bodyHtml = await renderSpotlightMarkdownToHtml(row.content)
  const base = getDomainUrl(request)
  const canonicalUrl = `${base}/novidades/${encodeURIComponent(slug)}`

  recordDynamicContentWebFetchSafe({ contentSlug: slug, request })

  return json({
    title: row.title,
    summary: row.summary,
    bannerImageUrl,
    bodyHtml,
    publishAt: row.publishAt.toISOString(),
    canonicalUrl,
  })
}

export default function NovidadeDetailRoute() {
  const data = useLoaderData<typeof loader>()

  return (
    <main className="novidades-main">
      <Container>
        <SpotlightDetail
          title={data.title}
          summary={data.summary}
          bannerImageUrl={data.bannerImageUrl}
          bodyHtml={data.bodyHtml}
          publishedAt={data.publishAt}
        />
      </Container>
    </main>
  )
}
